/**
 * The eight per-team Map component keys (§A10), in the order the gallery
 * (`B6-3-optional-features.svg`) shows them. These are the ONLY component
 * keys the Map settings surface exposes — the lead toggles each on or off
 * for their team. They map 1:1 onto the `team_features` registry entries
 * seeded in Phase 0 (`backend/src/components/registry.py`, `task_*`).
 *
 * Each carries a short, factual blurb that mirrors the wireframe verbatim.
 * The registry's own `description` is a fuller sentence; the gallery's
 * settings row uses a tight one-liner, so we keep the gallery copy here as
 * the display blurb (the registry copy still drives the admin Components
 * page on the scheduling side). Single source of truth for "which keys are
 * Map features" + "how they read on the toggle list".
 *
 * Brand firewall (§A9): no tier/plan language; each is a plain on/off the
 * lead controls — never a bundle, never an upsell. The strategic-outcome
 * term stays internal.
 */

/** A Map feature toggle, as the settings list renders it. */
export interface TaskFeatureSpec {
  /** The `team_features` registry key (matches `backend/.../registry.py`). */
  key: string;
  /** Short label shown in the toggle row (gallery-faithful). */
  label: string;
  /** One-line factual blurb shown beside the label (gallery-faithful). */
  blurb: string;
}

/**
 * The ordered Map feature list. The order matches the gallery's first four
 * rows (Dependencies · Notes · Reminders · Rewind), then the temporal layer
 * (Snapshots · Compare · Changes-since), which the design groups under the
 * same "Optional features" surface (§A10 "deeper time machine" row), and
 * finally Estimates (B2.12, #1043 — the coarse-bucket optional layer). The
 * `task_estimates` key flows through the same `useTeamFeatures` registry read
 * as the rest; the lead toggles it on/off per team like any other.
 */
export const TASK_FEATURE_SPECS: readonly TaskFeatureSpec[] = [
  {
    key: "task_dependencies",
    label: "Dependencies",
    blurb: "“comes before / blocks” arrows",
  },
  {
    key: "task_notes",
    label: "Notes",
    blurb: "per-task feedback threads",
  },
  {
    key: "task_reminders",
    label: "Reminders",
    blurb: "gentle nudges on dates & blockers",
  },
  {
    key: "task_rewind",
    label: "Rewind",
    blurb: "scrub the map back to any past day",
  },
  {
    key: "task_snapshots",
    label: "Snapshots",
    blurb: "pin a past state of the map with a label",
  },
  {
    key: "task_compare",
    label: "Compare dates",
    blurb: "see a “then vs now” diff between two points",
  },
  {
    key: "task_change_digest",
    label: "Changes since you last looked",
    blurb: "your own new work & state changes on return",
  },
  {
    key: "task_estimates",
    label: "Estimates",
    blurb: "coarse size buckets that roll up to the parent",
  },
  {
    key: "task_timed_notices",
    label: "Timed task messages on Telegram",
    blurb: "one message per person before a task starts",
  },
  {
    key: "task_notification_prefs",
    label: "Notification settings",
    blurb: "each person picks what to be nudged about, and where",
  },
] as const;

/** The set of Map feature keys, for filtering the full registry response. */
export const TASK_FEATURE_KEYS: ReadonlySet<string> = new Set(TASK_FEATURE_SPECS.map((s) => s.key));
